import Image from 'components/Common/Image/Image'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import useRebrandVersion from 'hooks/useRebrandVersion'
import DrawerDetailMobileImageCarousel from '../components/DrawerDetailMobileImageCarousel'
import {
  GalleryButtons,
  GridContainer,
  GridImageClickable,
  PRIMARY_GALLERY_IMAGE_CLASS,
  SECONDARY_GALLERY_IMAGE_CLASS,
} from '../components/TripPlanDetailDrawer.styles'
import { DrawerDetailHeroLoading } from '../components/drawerDetailHeroLayout'
import { getDrawerGalleryImageKey, useDrawerGalleryImages } from './useDrawerGalleryImages'

const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }
const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }

interface Args {
  images: Array<App.Image>
  resetKey: string
  isLoading: boolean
  mobileFullWidth: boolean
  viewGalleryLabel: string
}

/**
 * Builds the hero gallery shared by the trip planner detail drawers so callers
 * can place it above their own content (title, tabs) instead of inside a tab.
 */
export function useDrawerDetailHeroGallery({
  images,
  resetKey,
  isLoading,
  mobileFullWidth,
  viewGalleryLabel,
}: Args): ReactNode {
  const showModal = useContext(ModalContext)
  const isRebrand = useRebrandVersion() === 'rebrand'
  const galleryButtonKind = isRebrand ? 'primary' : 'secondary'

  const {
    availableImages,
    gridImages,
    imageCount,
    handleImageError,
  } = useDrawerGalleryImages({ images, resetKey })

  const openGalleryLightboxAtIndex = useCallback((index: number) => {
    showModal(<ImageGalleryLightbox images={availableImages} defaultIndex={index} />)
  }, [availableImages, showModal])

  const handleGalleryImageClick = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    openGalleryLightboxAtIndex(imageIndex)
  }, [openGalleryLightboxAtIndex])

  const openGalleryLightbox = useCallback(() => {
    openGalleryLightboxAtIndex(0)
  }, [openGalleryLightboxAtIndex])

  return useMemo(() => {
    if (isLoading) {
      return <DrawerDetailHeroLoading mobileFullWidth={mobileFullWidth} />
    }

    if (mobileFullWidth) {
      return availableImages.length > 0 && (
        <DrawerDetailMobileImageCarousel
          images={availableImages}
          onOpenLightbox={openGalleryLightboxAtIndex}
        />
      )
    }

    if (gridImages.length === 0) {
      return null
    }

    return (
      <GridContainer
        $imageCount={imageCount}
        $matchOfferPageAspect
      >
        {gridImages.map(({ image, lightboxIndex }, gridImageIndex) => (
          <GridImageClickable
            className={gridImageIndex === 0 ? PRIMARY_GALLERY_IMAGE_CLASS : SECONDARY_GALLERY_IMAGE_CLASS}
            key={image.id ?? image.url}
            onClick={handleGalleryImageClick}
            data-index={lightboxIndex}
          >
            {image.id && <ResponsiveImage
              id={image.id}
              fit="center"
              data-image-key={getDrawerGalleryImageKey(image)}
              onError={handleImageError}
              {...(gridImageIndex === 0 ? mainImageProps : defaultImageProps)}
            />}
            {image.url && <Image
              image={image}
              fit="center"
              data-image-key={getDrawerGalleryImageKey(image)}
              onError={handleImageError}
            />}
          </GridImageClickable>
        ))}
        {availableImages.length > 1 && (
          <GalleryButtons direction="horizontal" gap={8} horizontalAlign="end">
            <TextButton
              kind={galleryButtonKind}
              variant="dark"
              startIcon={<LineImagesIcon />}
              onClick={openGalleryLightbox}
            >
              {viewGalleryLabel}
            </TextButton>
          </GalleryButtons>
        )}
      </GridContainer>
    )
  }, [
    availableImages,
    galleryButtonKind,
    gridImages,
    handleGalleryImageClick,
    handleImageError,
    imageCount,
    isLoading,
    mobileFullWidth,
    openGalleryLightbox,
    openGalleryLightboxAtIndex,
    viewGalleryLabel,
  ])
}
